import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {addNotification} from '../../../../../../redux/actions/snackbar';
import {Modal} from '../../../../../../shared/components/modal/modal';
import {SnackbarNotificationType} from '../../../../../../shared/components/snackbar/model';
import {useStyles} from '../../../../../../shared/hooks/styles';
import {DesktopProps} from '../model';
import {EsiaRegCardFormScreen} from '../index';
import {EsiaRegCardMap} from '../../map';
import {Platform, View} from 'react-native';
import {CustomScrollView} from '../../../../../../shared/components/scrollView';
import EsiaActivateCard from '../../../../esiaActivateCard/esiaActivateCard';
import {
  cardApplicationIsExpired,
  esiaRegistrationInfoSelector,
  esiaUserHasNoCardsSelector,
} from '../../../../../../redux/selectors/login';
import {useAppDispatch} from '../../../../../../redux';
import {useSelector} from 'react-redux';
import {UserCardList} from '../components/userCardList/view';
import {useRegCardState} from '../../../esiaRegCardState/useRegCardState';
import {EsiaRegStep} from '../../../../../profile/desktop/components/applications/view';

export const EsiaRegCardFlowHandler = ({
  onClose,
  initialStep = EsiaRegStep.UsersList,
  readOnly,
}: DesktopProps & {initialStep?: EsiaRegStep}) => {
  const userHasNoCards = useSelector(esiaUserHasNoCardsSelector);
  const esiaRegInfo = useSelector(esiaRegistrationInfoSelector);
  const applicationIsExpired = useSelector(cardApplicationIsExpired);
  const dispatch = useAppDispatch();

  const [esiaRegStep, setEsiaRegStep] = useState(readOnly ? EsiaRegStep.Form : initialStep);
  const [isIssuePointSelectionVisible, setIsIssuePointSelectionVisible] = useState(false);

  const {addData, setParent} = useRegCardState();

  const handleGoBack = useCallback(() => {
    if (applicationIsExpired) {
      onClose();
    } else {
      setEsiaRegStep(EsiaRegStep.Form);
    }
  }, []);

  useEffect(() => {
    if (!esiaRegInfo) return;

    addData(esiaRegInfo);

    if (readOnly || initialStep === EsiaRegStep.Form) {
      setParent({...esiaRegInfo, checked: true});
    }
  }, []);

  useEffect(() => {
    if (applicationIsExpired) {
      setEsiaRegStep(EsiaRegStep.DeliveryType);

      dispatch(
        addNotification({
          message:
            'Срок резервации вашей карты истек или ранее поданное заявление было отклонено. Пожалуйста, выберите снова способ доставки для повторной подачи заявления на получение карты',
          type: SnackbarNotificationType.Info,
        }),
      );
    }
  }, [esiaRegInfo?.cardApplicationStatusId]);

  const styles = useStyles(({theme}) => ({
    modalTitle: {
      paddingTop: 20,
      ...theme.fonts.h3,
    },
    modalWrap: {
      paddingTop: 0,
      paddingBottom: 0,
      paddingHorizontal: 0,
      overflow: 'hidden',
      maxWidth: 878,
      width: '50%',
      margin: Platform.OS === 'ios' ? 1 : 0,
    },
    contentStyle: {
      paddingTop: 0,
    },
    backButton: {
      marginTop: 16,
    },
  }));

  let title = 'Оформление нефинансовой ЕСК';
  if (userHasNoCards && isIssuePointSelectionVisible) {
    title = 'Выбрать пункт выдачи';
  } else if (userHasNoCards && !applicationIsExpired) {
    title = 'Ваше заявление принято';
  } else if (esiaRegStep === EsiaRegStep.DeliveryType) {
    title = 'Выбрать способ получения';
  }

  const RegStepComponent = useMemo(() => {
    switch (esiaRegStep) {
      case EsiaRegStep.UsersList:
        return <UserCardList onClose={() => {}} onEsiaSubmit={() => setEsiaRegStep(EsiaRegStep.Form)} />;
      case EsiaRegStep.Form:
        return (
          <EsiaRegCardFormScreen
            readOnly={readOnly}
            onEsiaSubmit={() => {
              setEsiaRegStep(EsiaRegStep.DeliveryType);
            }}
          />
        );
      case EsiaRegStep.DeliveryType:
        return <EsiaRegCardMap onGoBack={handleGoBack} />;
      default:
        return null;
    }
  }, [esiaRegStep, readOnly]);

  return (
    <Modal
      title={title}
      onClose={onClose}
      titleStyle={styles.modalTitle}
      wrapStyle={styles.modalWrap}
      contentStyle={styles.contentStyle}>
      {userHasNoCards && !applicationIsExpired ? (
        <EsiaActivateCard onCloseModal={onClose} onIssuePointSelectionVisibleChange={setIsIssuePointSelectionVisible} />
      ) : (
        <View style={{maxHeight: '70dvh' as any}}>
          <CustomScrollView
            withRefresh={false}
            contentInsetAdjustmentBehavior='automatic'
            children={RegStepComponent}
          />
        </View>
      )}
    </Modal>
  );
};
